import { useEffect, useState } from 'react'
import { Gauge, Wrench } from 'lucide-react'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../../lib/shadcn/dialog'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { Textarea } from '../../lib/shadcn/textarea'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import { useRecordLastService, useRecordOdometerReading } from '../../hooks/backend/fleet'
import { writeErrorMessage } from '../../utils/writeError'
import { km, kmEntry, shortDate, type ServiceBoardRow } from '../../utils/serviceBoard'

/**
 * The two corrections the Service Schedule allows: the last service, and the
 * current odometer. Platform Requests #26 and #22.
 *
 * NEITHER EDITS A FIGURE. Each records a new dated row with the person's name
 * on it, and the board recomputes from the rows. That is why the button says
 * Record and not Edit: what the schedule said yesterday, and who changed it,
 * stays answerable. A wrong entry is put right by recording the right one.
 *
 * Nothing is computed here. Whether the car is due, and at what kilometres, is
 * read back from v_vehicle_service_board after the save.
 */

function today(): string {
  // The date in Johannesburg, which is the operator's day.
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Johannesburg' })
}

const trigger =
  'mt-1 rounded text-xs font-medium text-[hsl(var(--brand))] underline-offset-2 outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring'

export function LastServiceDialog({ row, onSaved }: { row: ServiceBoardRow; onSaved: () => void }) {
  const [open, setOpen] = useState(false)
  const [date, setDate] = useState('')
  const [odo, setOdo] = useState('')
  const [provider, setProvider] = useState('')
  const [notes, setNotes] = useState('')
  const save = useRecordLastService()
  const parsed = kmEntry(odo)

  useEffect(() => {
    if (open) {
      setDate('')
      setOdo('')
      setProvider('')
      setNotes('')
    }
  }, [open])

  async function submit() {
    if (parsed === null) return
    try {
      await save.trigger({
        vehicle_id: row.vehicle_id,
        serviced_on: date,
        odometer_km: parsed,
        provider_name: provider.trim() || null,
        notes: notes.trim() || null,
      }).result
      toast.success(`Service recorded on ${row.plate ?? 'the vehicle'}`, {
        description: `Next service is measured from ${km(parsed)}.`,
      })
      setOpen(false)
      onSaved()
    } catch (err) {
      toast.error('Could not record that service', { description: writeErrorMessage(err) })
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button type="button" className={trigger} aria-label={`Record the last service on ${row.plate ?? 'this vehicle'}`}>
          Record service
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Last service, {row.plate ?? 'vehicle'}</DialogTitle>
          <DialogDescription>
            Record the date and the odometer as they read on the service stamp or the invoice. The
            next service is measured from this, and it replaces an inferred figure on the board.
            {row.last_serviced_on
              ? ` On file now: ${shortDate(row.last_serviced_on)} at ${km(row.baseline_km)}${row.baseline_provisional ? ', inferred' : ''}.`
              : ' There is no last service on file for this car.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor={`svc-date-${row.vehicle_id}`}>Serviced on</Label>
              <Input
                id={`svc-date-${row.vehicle_id}`}
                type="date"
                max={today()}
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={`svc-km-${row.vehicle_id}`}>Odometer at service</Label>
              <Input
                id={`svc-km-${row.vehicle_id}`}
                inputMode="numeric"
                value={odo}
                onChange={(e) => setOdo(e.target.value)}
                placeholder="e.g. 152 011"
              />
            </div>
          </div>
          {odo.trim() !== '' && parsed === null && (
            <p className="text-xs text-red-700 dark:text-red-400">
              Write the odometer as a whole number of kilometres, spaces or commas allowed.
            </p>
          )}
          <div className="space-y-1.5">
            <Label htmlFor={`svc-prov-${row.vehicle_id}`}>
              Workshop <span className="ml-1 font-normal text-muted-foreground">optional</span>
            </Label>
            <Input
              id={`svc-prov-${row.vehicle_id}`}
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
              placeholder="Who did the service"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`svc-notes-${row.vehicle_id}`}>
              Note <span className="ml-1 font-normal text-muted-foreground">optional</span>
            </Label>
            <Textarea
              id={`svc-notes-${row.vehicle_id}`}
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Where the figure came from"
            />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" disabled={save.loading || !date || parsed === null} onClick={submit}>
            {save.loading ? <Spinner className="mr-2 h-4 w-4" /> : <Wrench className="mr-2 h-4 w-4" />}
            Record service
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function OdometerDialog({ row, onSaved }: { row: ServiceBoardRow; onSaved: () => void }) {
  const [open, setOpen] = useState(false)
  const [date, setDate] = useState('')
  const [odo, setOdo] = useState('')
  const [note, setNote] = useState('')
  const save = useRecordOdometerReading()
  const parsed = kmEntry(odo)

  useEffect(() => {
    if (open) {
      setDate(today())
      setOdo('')
      setNote('')
    }
  }, [open])

  async function submit() {
    if (parsed === null) return
    try {
      await save.trigger({
        vehicle_id: row.vehicle_id,
        odometer_km: parsed,
        observed_on: date || null,
        note: note.trim() || null,
      }).result
      toast.success(`Odometer recorded on ${row.plate ?? 'the vehicle'}`, { description: km(parsed) })
      setOpen(false)
      onSaved()
    } catch (err) {
      toast.error('Could not record that reading', { description: writeErrorMessage(err) })
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button type="button" className={trigger} aria-label={`Correct the odometer on ${row.plate ?? 'this vehicle'}`}>
          Correct
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Odometer, {row.plate ?? 'vehicle'}</DialogTitle>
          <DialogDescription>
            Record what the dashboard reads. Drivers&apos; inspection readings and the Cartrack
            tracker keep this figure moving; use this when they are wrong or missing. Your reading is
            kept with your name and the date, and the drivers&apos; readings stay on file.
            {row.current_km !== null
              ? ` On the board now: ${km(row.current_km)}, ${shortDate(row.odometer_observed_at)}.`
              : ' There is no usable reading on the board for this car.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor={`odo-km-${row.vehicle_id}`}>Odometer</Label>
              <Input
                id={`odo-km-${row.vehicle_id}`}
                inputMode="numeric"
                value={odo}
                onChange={(e) => setOdo(e.target.value)}
                placeholder="e.g. 111 629"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={`odo-date-${row.vehicle_id}`}>Read on</Label>
              <Input
                id={`odo-date-${row.vehicle_id}`}
                type="date"
                max={today()}
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>
          </div>
          {odo.trim() !== '' && parsed === null && (
            <p className="text-xs text-red-700 dark:text-red-400">
              Write the odometer as a whole number of kilometres, spaces or commas allowed.
            </p>
          )}
          <div className="space-y-1.5">
            <Label htmlFor={`odo-note-${row.vehicle_id}`}>
              Where it came from <span className="ml-1 font-normal text-muted-foreground">optional</span>
            </Label>
            <Textarea
              id={`odo-note-${row.vehicle_id}`}
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. servicing tracking sheet, or read at the yard"
            />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" disabled={save.loading || parsed === null} onClick={submit}>
            {save.loading ? <Spinner className="mr-2 h-4 w-4" /> : <Gauge className="mr-2 h-4 w-4" />}
            Record reading
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
